// frontend/src/utils/adVisitor.js
//
// "Did this visit arrive from one of our Google Search ads?" - read-only
// helpers over the acquisition codes CaptureAcquisition.js captures (#100).
//
// COPY ONLY. The answer may change WORDS on a page (the ad-visitor hero), never
// a price, a discount, a trial length or an entitlement: the codes ride in the
// address bar and in sessionStorage, so any visitor can set one by hand.
//
// WHERE THE CODE COMES FROM, in order:
//   1. sessionStorage `acq_s` / `acq_c` - written by
//      utils/acquisitionCapture.js (at boot from src/index.js, and again from
//      CaptureAcquisition), first-writer-wins, so the first tagged arrival of
//      the visit owns it;
//   2. the current URL's `s` / `c` - for a tab whose storage is blocked, or a
//      boot that skipped the synchronous capture: the code is then only in the
//      address bar until CaptureAcquisition's effect strips it.
// A stored value that fails validation is treated as absent, not as an answer.
//
// PURE: never writes, never throws (a blocked/hostile storage or a malformed
// URL is a "no"), and safe under the prerenderer and any window-less render.
//
// The ad-group codes mirror backend/app/core/acquisition.py: every code whose
// canonical source starts with `gads-`. The frontend otherwise treats the codes
// as opaque; this list is the one deliberate exception, and it names codes, not
// what they mean.

/** The `s` codes of the Google Search ad groups (canonical source `gads-*`). */
export const GOOGLE_ADS_CODES = Object.freeze([
  "e3", // gads-lawyers
  "u6", // gads-accountants
  "x5", // gads-insurance
  "b8", // gads-marketers
  "f2", // gads-dentists
  "c7", // gads-dog-trainers
  "a9", // gads-web-dev
  "r8", // gads-buy-leads (the homepage ad group)
]);

/** The `c` values the Google Ads final URLs carry. */
export const GOOGLE_ADS_CAMPAIGN_CODES = Object.freeze(["gs1"]);

// Twin of utils/acquisitionCapture.js's CODE_RE (adVisitor.test.js fails if the two
// drift): a code is 1-8 ASCII alphanumerics, case-sensitive.
const CODE_RE = /^[A-Za-z0-9]{1,8}$/;

const isValidCode = (value) => typeof value === "string" && CODE_RE.test(value);

/** A valid stored code under `key`, or null (absent, invalid or unreadable). */
const readStored = (key) => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    const value = window.sessionStorage.getItem(key);
    return isValidCode(value) ? value : null;
  } catch (e) {
    return null;
  }
};

/** A valid `param` from the current address bar, or null. */
const readUrl = (param) => {
  try {
    if (typeof window === "undefined" || !window.location) return null;
    const value = new URLSearchParams(window.location.search).get(param);
    return isValidCode(value) ? value : null;
  } catch (e) {
    return null;
  }
};

/** This session's validated code for `param` (`s` or `c`), or null. */
const sessionCode = (storageKey, param) => readStored(storageKey) ?? readUrl(param);

/**
 * This session's validated acquisition codes, whatever their source - the same
 * storage-first, URL-fallback read the two predicates below use, without the
 * ads filter. For REPORTING (the engagement beacon tags a page view with them);
 * like everything here, never for a price, discount or entitlement.
 *
 * @returns {{s: string|null, c: string|null}}
 */
export function getAcquisitionCodes() {
  return { s: sessionCode("acq_s", "s"), c: sessionCode("acq_c", "c") };
}

/**
 * This session's acquisition `s` code when it is a Google Search ad group.
 *
 * @returns {string|null} one of GOOGLE_ADS_CODES, or null for no code, an
 *   invalid code, or any non-ads code (organic posts, partners, Meta ads).
 */
export function getAdSourceCode() {
  const code = sessionCode("acq_s", "s");
  return code !== null && GOOGLE_ADS_CODES.includes(code) ? code : null;
}

/**
 * True when this visit came from a Google Search ad: an ad-group `s` code, OR
 * the Google Ads campaign `c` code on its own (a final URL that lost its `s`).
 *
 * @returns {boolean}
 */
export function isPaidAdVisitor() {
  if (getAdSourceCode() !== null) return true;
  const campaign = sessionCode("acq_c", "c");
  return campaign !== null && GOOGLE_ADS_CAMPAIGN_CODES.includes(campaign);
}
